import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../Utils/Axios';
import { resolveMediaUrl } from '../Utils/media';
import { useRegion } from '../context/RegionContext';
import {
  getRegionFlagUrl,
  getRegionLocationLabel,
  getRegionCountryOptions,
  getRegionCountryRecord,
  getRegionStateLabel,
  getRegionStateLabelPlural,
  getRegionStateOptions,
  getRegionStateRecord,
} from '../Utils/region';

const resolveHospitalLogo = (logo) => logo || 'assets/images/bg/company-logo/company-01.png';
const monthName = (dateString) => {
  const d = new Date(dateString);
  return Number.isNaN(d.getTime()) ? 'January' : d.toLocaleDateString('en-US', { month: 'long' });
};
const dayNumber = (dateString) => {
  const d = new Date(dateString);
  return Number.isNaN(d.getTime()) ? 1 : d.getDate();
};
const blogFallbackImages = [
  '/assets/images/blog/blog-img-01.png',
  '/assets/images/blog/blog-img-02.png',
  '/assets/images/blog/blog-img-03.png',
  '/assets/images/blog/blog-img-04.png',
  '/assets/images/blog/blog-img-05.png',
  '/assets/images/blog/blog-img-06.png',
];
const Home = () => {
  const navigate = useNavigate();
  const locationTrackRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [jobSeekersCount, setJobSeekersCount] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchState, setSearchState] = useState('');
  const { region } = useRegion();
  const heroVideos = useMemo(() => {
    if (region === 'af') {
      return [
        '/assets/video/africa1.mp4',
        '/assets/video/africa2.mp4',
        '/assets/video/africa3.mp4',
        '/assets/video/africa4.mp4',
        '/assets/video/africa5.mp4',
      ];
    }
    return ['/assets/video/banner1.mp4', '/assets/video/banner2.mp4', '/assets/video/banner3.mp4'];
  }, [region]);

  const categoryIcons = useMemo(
    () => [
      '/assets/images/icon/medical.svg',
      '/assets/images/icon/technology.svg',
      '/assets/images/icon/administration.svg',
      '/assets/images/icon/account-finance.svg',
      '/assets/images/icon/marketing.svg',
      '/assets/images/icon/education.svg',
      '/assets/images/icon/receptionist-06.svg',
      '/assets/images/icon/non-profit-org.svg',
      '/assets/images/icon/transportation.svg',
      '/assets/images/icon/architect.svg',
    ],
    []
  );
  const categoryIconMap = useMemo(
    () => ({
      nursing: '/assets/images/nursing.svg',
      nurse: '/assets/images/nursing.svg',
      'allied health': '/assets/images/allied-health.svg',
      'allied-health': '/assets/images/allied-health.svg',
      leadership: '/assets/images/leadership.svg',
      'support services': '/assets/images/support-service.svg',
      'support service': '/assets/images/support-service.svg',
      'support-service': '/assets/images/support-service.svg',
      physician: '/assets/images/physision.png',
      physision: '/assets/images/physision.png',
      doctors: '/assets/images/physision.png',
      doctor: '/assets/images/physision.png',
    }),
    []
  );
  const stateNames = useMemo(
    () => (region === 'af' ? getRegionCountryOptions(region) : getRegionStateOptions(region)),
    [region]
  );
  const stateLabel = useMemo(() => (region === 'af' ? 'Country' : getRegionStateLabel(region)), [region]);
  const stateLabelPlural = useMemo(() => getRegionStateLabelPlural(region), [region]);
  const locationLabel = useMemo(() => getRegionLocationLabel(region), [region]);
  const featuredStates = useMemo(() => {
    if (!jobs || jobs.length === 0) return [];

    const counts = new Map();
    jobs.forEach((job) => {
      const rawName =
        region === 'af' ? String(job?.country || job?.state || '').trim() : String(job?.state || '').trim();
      if (!rawName) return;
      const key = rawName.toLowerCase();
      counts.set(key, { name: rawName, count: (counts.get(key)?.count || 0) + 1 });
    });

    return Array.from(counts.values())
      .map((row) => {
        const match =
          region === 'af'
            ? getRegionCountryRecord(region, row.name)
            : getRegionStateRecord(region, row.name);
        return {
          state: match?.name || row.name,
          record: match || null,
          jobs: row.count,
        };
      })
      .filter((item) => item.state)
      .sort((a, b) => b.jobs - a.jobs)
      .slice(0, 25);
  }, [jobs, region]);

  useEffect(() => {
    const loadHomeData = async () => {
      try {
        const [categoryRes, jobsRes, hospitalsRes, blogsRes, statsRes] = await Promise.all([
          api.get('/api/categories'),
          api.get('/api/hospital/public-jobs'),
          api.get('/api/hospital/public-hospitals'),
          api.get('/api/blogs'),
          api.get('/api/user/public-stats'),
        ]);
        setCategories(categoryRes.data?.categories || []);
        setJobs(jobsRes.data?.jobs || []);
        setHospitals(hospitalsRes.data?.hospitals || []);
        setBlogs(blogsRes.data?.blogs || []);
        setJobSeekersCount(Number(statsRes.data?.stats?.totalJobSeekers) || 0);
      } catch (error) {
        setCategories([]);
        setJobs([]);
        setHospitals([]);
        setBlogs([]);
        setJobSeekersCount(0);
      }
    };

    loadHomeData();
  }, []);

  const categoryCards = useMemo(() => {
    const countByCategory = {};
    jobs.forEach((job) => {
      const key = (job?.category || '').trim().toLowerCase();
      if (!key) return;
      countByCategory[key] = (countByCategory[key] || 0) + 1;
    });

    return categories.map((category, index) => {
      const normalizedName = String(category?.name || '').trim().toLowerCase();
      const mappedIcon = categoryIconMap[normalizedName];
      return {
        id: category._id || category.name || index,
        name: category.name,
        count: countByCategory[(category.name || '').trim().toLowerCase()] || 0,
      icon: mappedIcon || categoryIcons[index % categoryIcons.length] || '/assets/images/icon/category.svg',
        serial: String(index + 1).padStart(2, '0'),
      };
    });
  }, [categories, jobs, categoryIcons, categoryIconMap]);

  const recruiterCards = useMemo(() => {
    const jobCountsById = new Map();
    const jobCountsByName = new Map();

    jobs.forEach((job) => {
      const idKey = String(job?.hospitalId || '').trim();
      const nameKey = String(job?.hospitalName || '').trim();

      if (idKey) {
        jobCountsById.set(idKey, (jobCountsById.get(idKey) || 0) + 1);
      }
      if (nameKey) {
        jobCountsByName.set(nameKey, (jobCountsByName.get(nameKey) || 0) + 1);
      }
    });

    const fromHospitals = (hospitals || []).map((hospital, index) => {
      const idKey = String(hospital?._id || '').trim();
      const nameKey = String(hospital?.name || '').trim();
      const openJobs =
        (idKey ? jobCountsById.get(idKey) : 0) ||
        (nameKey ? jobCountsByName.get(nameKey) : 0) ||
        0;

      return {
        key: idKey || nameKey || `hospital-${index}`,
        hospitalName: hospital?.name || 'Hospital',
        hospitalLogo: resolveHospitalLogo(hospital?.logo),
        location:
          region === 'af'
            ? [hospital?.city, hospital?.state, hospital?.country].filter(Boolean).join(', ') || locationLabel
            : [hospital?.city, hospital?.state].filter(Boolean).join(', ') || locationLabel,
        openJobs,
      };
    });

    if (fromHospitals.length > 0) {
      return fromHospitals
        .sort((a, b) => b.openJobs - a.openJobs)
        .slice(0, 6);
    }

    const fromJobs = Array.from(jobCountsByName.entries())
      .map(([name, count], index) => ({
        key: `job-${name}-${index}`,
        hospitalName: name || 'Hospital',
        hospitalLogo: resolveHospitalLogo(''),
        location: locationLabel,
        openJobs: count,
      }))
      .sort((a, b) => b.openJobs - a.openJobs)
      .slice(0, 6);

    if (fromJobs.length > 0) {
      return fromJobs;
    }

    return Array.from({ length: 6 }).map((_, idx) => ({
      key: `placeholder-${idx + 1}`,
      hospitalName: `Top Hospital ${idx + 1}`,
      hospitalLogo: resolveHospitalLogo(''),
      location: locationLabel,
      openJobs: 0,
    }));
  }, [jobs, hospitals]);

  const homeBlogCards = useMemo(
    () =>
      (blogs || []).slice(0, 3).map((blog, index) => ({
        ...blog,
        image: resolveMediaUrl(blog.coverImage) || blogFallbackImages[index % blogFallbackImages.length],
      })),
    [blogs]
  );

  const bannerStats = useMemo(() => {
    const now = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    const newJobsCount = jobs.filter((job) => {
      const createdAtTs = new Date(job?.createdAt).getTime();
      return !Number.isNaN(createdAtTs) && now - createdAtTs <= sevenDaysMs;
    }).length;

    return {
      liveJobs: jobs.length,
      hospitals: hospitals.length,
      jobSeekers: jobSeekersCount,
      newJobs: newJobsCount,
    };
  }, [jobs, hospitals, jobSeekersCount]);

  const buildHeroQuery = (override = {}) => {
    const keyword = String(override.keyword ?? searchKeyword).trim();
    const state = String(override.state ?? searchState).trim();
    const params = new URLSearchParams();
    if (keyword) params.set('q', keyword);
    if (state) params.set('state', state);
    return params.toString();
  };

  const handleHeroRemote = () => {
    const keyword = searchKeyword.trim();
    const params = new URLSearchParams();
    params.set('q', keyword ? `${keyword} remote` : 'remote');
    navigate(`/job-listing?${params.toString()}`);
  };

  const handleHeroViewAll = () => {
    const query = buildHeroQuery();
    navigate(query ? `/job-listing?${query}` : '/job-listing');
  };

  const scrollLocationTrack = (direction = 'next') => {
    const container = locationTrackRef.current;
    if (!container) return;
    const step = 310;
    const delta = direction === 'prev' ? -step : step;
    container.scrollBy({ left: delta, behavior: 'smooth' });
  };

  useEffect(() => {
    const container = locationTrackRef.current;
    if (!container) return undefined;

    const tick = () => {
      const maxLeft = container.scrollWidth - container.clientWidth;
      if (maxLeft <= 0) return;

      if (container.scrollLeft >= maxLeft - 4) {
        container.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }

      container.scrollBy({ left: 310, behavior: 'smooth' });
    };

    const intervalId = window.setInterval(tick, 2400);
    return () => window.clearInterval(intervalId);
  }, [featuredStates.length]);

  return (
    <>
      <style>
        {`
          /* 
            Teaching note:
            The original theme adds a very dark overlay with the hero1 ::before layer.
            We override it only on this page so the banner video stays visible.
          */
          .hero1::before {
            background: linear-gradient(90deg, rgba(8, 12, 26, 0.52) 0%, rgba(8, 12, 26, 0.3) 55%, rgba(8, 12, 26, 0.18) 100%) !important;
            pointer-events: none;
          }
          .hero1 {
            position: relative;
            overflow: hidden;
            min-height: clamp(420px, 70vh, 820px);
          }
          @media (max-width: 767px) {
            .hero1 {
              min-height: clamp(360px, 60vh, 620px);
            }
          }
          .hero-video-stack {
            position: absolute;
            inset: 0;
            z-index: 0;
            pointer-events: none;
          }
          .hero-video {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            max-width: none;
            opacity: 0;
            animation: heroVideoCycle 24s linear infinite;
            filter: saturate(1.05) contrast(1.02);
          }
          .hero1 .hero-content {
            position: relative;
            z-index: 2;
          }
          .hero1.africa-hero .hero-video {
            filter: brightness(1.18) saturate(1.12) contrast(1.05);
          }
          .hero-video::selection {
            background: transparent;
          }
          @keyframes heroVideoCycle {
            0% { opacity: 0; }
            5% { opacity: 1; }
            30% { opacity: 1; }
            36% { opacity: 0; }
            100% { opacity: 0; }
          }
          .hero1 .hero-content .job-search-area.hpwp-hero-filter {
            background: #ffffff;
            border: 1px solid rgba(255, 255, 255, 0.2);
            box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.25);
            max-width: 1180px;
            position: relative;
            z-index: 2;
          }
          .hero1 .hero-content .job-search-area.hpwp-hero-filter form {
            gap: 14px;
            padding: 12px;
          }
          .hero1 .hero-content .job-search-area.hpwp-hero-filter .form-inner {
            flex: 1 1 0;
          }
          .hero1 .hero-content .job-search-area.hpwp-hero-filter .form-inner.job-title {
            position: relative;
          }
          .hero1 .hero-content .job-search-area.hpwp-hero-filter .form-inner.job-title input {
            background: #EFF3F2;
            border: 1px solid #DFE9E5;
            border-radius: 6px;
            padding: 0 48px 0 18px;
            color: var(--title-color1);
          }
          .hero1 .hero-content .job-search-area.hpwp-hero-filter .form-inner.job-title input::placeholder {
            font-size: 11px;
            line-height: 1.2;
          }
          .hero1 .hero-content .job-search-area.hpwp-hero-filter .form-inner.job-title .hpwp-hero-icon {
            position: absolute;
            right: 16px;
            top: 50%;
            transform: translateY(-50%);
            pointer-events: none;
            display: inline-flex;
            align-items: center;
          }
          .hero1 .hero-content .job-search-area.hpwp-hero-filter .form-inner.category select {
            background: #EFF3F2;
            border: 1px solid #DFE9E5;
            border-radius: 6px;
            height: 60px;
            padding: 0 48px 0 18px;
            color: var(--title-color1);
            width: 100%;
            appearance: none;
            -webkit-appearance: none;
            -moz-appearance: none;
          }
          .hero1 .hero-content .job-search-area.hpwp-hero-filter .form-inner.category span svg path {
            fill: var(--title-color1);
          }
          .hero1 .hero-content .job-search-area.hpwp-hero-filter .hero-filter-btn {
            height: 60px;
            padding: 0 22px;
            border-radius: 6px;
            background: linear-gradient(
              135deg,
              var(--hpwp-accent-light) 0%,
              var(--hpwp-accent) 55%,
              var(--hpwp-accent-dark) 100%
            ) !important;
            border: 1px solid var(--hpwp-accent) !important;
            color: #ffffff !important;
            white-space: nowrap;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
          }
          .hero1 .hero-content .job-search-area.hpwp-hero-filter .hero-filter-btn:hover {
            background: linear-gradient(
              135deg,
              var(--hpwp-accent) 0%,
              var(--hpwp-accent-dark) 100%
            ) !important;
            border-color: var(--hpwp-accent-dark) !important;
          }
          .home1-category .single-category .category-top .icon {
            width: 88px;
            height: 88px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 20px;
            background: #ffffff;
            border: 1px solid rgba(201, 154, 24, 0.25);
            box-shadow: 0 10px 20px rgba(201, 154, 24, 0.18);
          }
          .home1-category .single-category .category-top .icon img {
            width: 56px;
            height: 56px;
            object-fit: contain;
          }
          .home1-category {
            background: linear-gradient(180deg, rgba(201, 154, 24, 0.08) 0%, rgba(201, 154, 24, 0.02) 100%);
          }
          .home1-category .single-category {
            border: 1px solid rgba(201, 154, 24, 0.22);
            box-shadow: 0 14px 30px rgba(21, 42, 80, 0.1);
          }
          .home1-category .single-category:hover {
            transform: translateY(-10px);
          }
        `}
      </style>
      <div
        className={`hero1 ${region === 'af' ? 'africa-hero' : ''}`}
        style={{
          // We clear the old CSS background image here because the video will become
          // the new visual background for the banner.
          backgroundImage: 'none',
          overflow: 'hidden',
        }}
      >
        {/* 
          Teaching note:
          Files inside `frontend/public` are served directly from the site root.
          That is why these videos use `/assets/video/...` instead of imports.
          
          If you replace the video later, keep the file in `public/assets/video/`
          and then update the `src` below.
        */}
        <div className="hero-video-stack" aria-hidden="true">
          {heroVideos.map((src, index) => (
            <video
              key={src}
              className="hero-video"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              style={{ animationDelay: `${index * 8}s` }}
            >
              <source src={src} type="video/mp4" />
            </video>
          ))}
        </div>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="hero-content">
                <h1>
                Career’s <span>Opportunity.</span>
                </h1>
                <p>
                  <span>2400</span> Peoples are daily search in this portal,{" "}
                  <span>100</span> user added job portal!
                </p>
                <div className="counter-area">
                  <div className="row g-lg-4 g-md-5 gy-5 justify-content-center">
                    <div className="col-lg-3 col-sm-6">
                      <div className="counter-single">
                        <div className="counter-icon">
                          <img src="assets/images/icon/job2.svg" alt="image" />
                        </div>
                        <div className="coundown">
                          <p>Live Jobs</p>
                          <div className="d-flex align-items-center gap-2">
                            <h3>{bannerStats.liveJobs}</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-sm-6">
                      <div className="counter-single">
                        <div className="counter-icon">
                          <img src="assets/images/icon/companies.svg" alt="image" />
                        </div>
                        <div className="coundown">
                          <p>Hospitals</p>
                          <div className="d-flex align-items-center gap-2">
                            <h3>{bannerStats.hospitals}</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-sm-6">
                      <div className="counter-single">
                        <div className="counter-icon">
                          <img src="assets/images/icon/candidates.svg" alt="image" />
                        </div>
                        <div className="coundown">
                          <p>Job Seekers</p>
                          <div className="d-flex align-items-center gap-2">
                            <h3>{bannerStats.jobSeekers}</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-sm-6">
                      <div className="counter-single">
                        <div className="counter-icon">
                          <img src="assets/images/icon/new-jobs.svg" alt="image" />
                        </div>
                        <div className="coundown">
                          <p>New Jobs</p>
                          <div className="d-flex align-items-center gap-2">
                            <h3>{bannerStats.newJobs}</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="job-search-area hpwp-hero-filter">
                  <form onSubmit={(e) => e.preventDefault()}>
                    <div className="form-inner job-title">
                      <input
                      
                        name="heroKeyword"
                        type="text"
                        placeholder="Search Jobs by Keyword"
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                      />
                      <span className="hpwp-hero-icon" aria-hidden="true">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M15.5 15.5L21 21"
                            stroke="#0f2455"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <circle
                            cx="10.5"
                            cy="10.5"
                            r="6.5"
                            stroke="#0f2455"
                            strokeWidth="2"
                          />
                        </svg>
                      </span>
                    </div>
                    <div className="form-inner category" style={{ position: 'relative' }}>
                      <select
                        name="heroState"
                        className="select1"
                        value={searchState}
                        onChange={(e) => setSearchState(e.target.value)}
                      >
                        <option value="">{`Search Jobs by ${stateLabel}`}</option>
                        {stateNames.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                      <span
                        style={{
                          position: 'absolute',
                          right: '25px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                        }}
                      >
                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M1.41 0.589966L6 5.16997L10.59 0.589966L12 1.99997L6 7.99997L0 1.99997L1.41 0.589966Z"
                            fill="#1C2120"
                          />
                        </svg>
                      </span>
                    </div>
                    <div className="form-inner">
                      <button
                        type="button"
                        className="primry-btn-2 hero-filter-btn"
                        onClick={handleHeroRemote}
                      >
                        <img src="assets/images/icon/search-icon.svg" alt="" /> Remote Jobs
                      </button>
                    </div>
                    <div className="form-inner">
                      <button
                        type="button"
                        className="primry-btn-2 hero-filter-btn"
                        onClick={handleHeroViewAll}
                      >
                        <img src="assets/images/icon/search-icon.svg" alt="" /> View All Jobs
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>





      <div className="home1-category pt-120 mb-120">
        <div className="container">
          <div className="row mb-60">
            <div className="col-12 d-flex flex-wrap align-items-end justify-content-md-between justify-content-start gap-3">
              <div className="section-title1">
                <h2>
                  Job <span>Categories</span>
                </h2>
                <p>Dynamic categories from admin panel with live hiring activity.</p>
              </div>
              <div className="explore-btn">
                <Link to="/find-job">
                  Explore More{" "}
                  <span>
                    <img src="assets/images/icon/explore-elliose.svg" alt="" />
                  </span>
                </Link>
              </div>
            </div>
          </div>
          <div className="row row-cols-xxl-5 row-cols-xl-4 row-cols-md-3 row-cols-sm-2 row-cols-1 g-3 cf justify-content-center">
            {categoryCards.length === 0 ? (
              <div className="col-12">
                <div className="single-category" style={{ textAlign: 'center' }}>
                  <div className="category-content">
                    <h5>No category available yet.</h5>
                    <p>Add categories from admin panel to show here.</p>
                  </div>
                </div>
              </div>
            ) : (
              categoryCards.map((category) => (
                <div className="col" key={category.id}>
                  <Link
                    to={`/job-listing?category=${encodeURIComponent(category.name)}`}
                    className="single-category"
                    style={{
                      display: 'block',
                      border: '1px solid #e7ecf3',
                      borderRadius: 12,
                      background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
                      boxShadow: '0 10px 25px rgba(21, 42, 80, 0.06)',
                    }}
                  >
                    <div className="category-top">
                      <div className="icon">
                        <img src={category.icon} alt={category.name} />
                      </div>
                      <div className="sl-no">
                        <h6>{category.serial}</h6>
                      </div>
                    </div>
                    <div className="category-content">
                      <h5>{category.name}</h5>
                      <p>
                        Job Available: <span>{category.count}</span>
                      </p>
                    </div>
                  </Link>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

<div className="home1-featured-area mb-120">
  <div className="container">
    <div className="row mb-60">
      <div className="col-12 d-flex flex-wrap align-items-end justify-content-md-between justify-content-start gap-3">
        <div className="section-title1">
          <h2>
            <span>Hot</span> Jobs
          </h2>
          <p>Latest active roles from hospitals, updated automatically.</p>
        </div>
        <div className="explore-btn">
          <Link to="/job-listing">
            Explore More{" "}
            <span>
              <img src="assets/images/icon/explore-elliose.svg" alt="" />
            </span>
          </Link>
        </div>
      </div>
    </div>
    <div className="row g-4">
      {jobs.length === 0 ? (
        <div className="col-12">
          <div className="feature-card">
            <div className="company-area">
              <div className="company-details">
                <div className="name-location">
                  <h5>No hot jobs available right now.</h5>
                  <p>Please check back soon.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        jobs.slice(0, 6).map((job) => (
          <div className="col-xl-4 col-lg-6" key={job._id}>
            <div className="feature-card">
              <div className="company-area">
                <div className="logo">
                  <img
                    src={job.hospitalLogo || "assets/images/bg/company-logo/company-01.png"}
                    alt={job.hospitalName || "Hospital"}
                  />
                </div>
                <div className="company-details">
                  <div className="name-location">
                    <h5>
                      <Link to={`/job-details/${job._id}`}>{job.title}</Link>
                    </h5>
                    <p>{job.employmentType || "Full Time"}</p>
                  </div>
                  <div className="bookmark">
                    <i className="bi bi-bookmark" />
                  </div>
                </div>
              </div>
              <div className="job-discription">
                <ul>
                  <li>
                    <img src="assets/images/icon/arrow2.svg" alt="" />
                    <p>
                      <span className="title">Salary:</span> ${job.salaryMin || 0}-${job.salaryMax || 0}{" "}
                      <span className="time">Per month</span>
                    </p>
                  </li>
                  <li>
                    <img src="assets/images/icon/arrow2.svg" alt="" />
                    <p>
                      <span className="title">Hospital:</span> <span>{job.hospitalName || "Hospital"}</span>
                    </p>
                  </li>
                  <li>
                    <img src="assets/images/icon/arrow2.svg" alt="" />
                    <p>
                      <span className="title">Deadline:</span>{" "}
                      <span>{job.deadline ? new Date(job.deadline).toLocaleDateString() : "Open"}</span>
                    </p>
                  </li>
                </ul>
              </div>
              <div className="job-type-apply">
                <div className="apply-btn">
                  <Link to={`/job-details/${job._id}`}>
                    <span>
                      <img src="assets/images/icon/apply-ellipse.svg" alt="" />
                    </span>
                    Apply Now
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
</div>



<div className="home1-work-process mb-120">
  <div className="container">
    <div className="row mb-60">
      <div className="col-12 d-flex justify-content-center">
        <div className="section-title1 text-center">
          <h2>
            JOBES Working <span>Process</span>
          </h2>
          <p>To choose your trending job dream &amp; to make future bright.</p>
        </div>
      </div>
    </div>
    <div className="row g-lg-4 gy-5 justify-content-center">
      <div className="col-xl-3 col-lg-4 col-sm-6">
        <div className="single-work-process one text-center">
          <div className="icon">
            <img src="assets/images/icon/account-create.svg" alt="" />
          </div>
          <div className="work-content">
            <h5>
              <a href="register.html">Account Create</a>
            </h5>
            <p>To create your account be confident &amp; safely.</p>
          </div>
        </div>
      </div>
      <div className="col-xl-3 col-lg-4 col-sm-6">
        <div className="single-work-process two text-center">
          <div className="icon">
            <img src="assets/images/icon/create-resume.svg" alt="" />
          </div>
          <div className="work-content">
            <h5>
              <a href="edit-profile.html">Create Resume</a>
            </h5>
            <p>To create your account be confident &amp; safely.</p>
          </div>
        </div>
      </div>
      <div className="col-xl-3 col-lg-4 col-sm-6">
        <div className="single-work-process one text-center">
          <div className="icon">
            <img src="assets/images/icon/job-find.svg" alt="" />
          </div>
          <div className="work-content">
            <h5>
              <a href="job-listing1.html">Find Jobs </a>
            </h5>
            <p>To create your account be confident &amp; safely.</p>
          </div>
        </div>
      </div>
      <div className="col-xl-3 col-lg-4 col-sm-6">
        <div className="single-work-process two text-center">
          <div className="icon">
            <img src="assets/images/icon/job-apply.svg" alt="" />
          </div>
          <div className="work-content">
            <h5>
              <a href="job-listing1.html">Apply Jobs</a>
            </h5>
            <p>To create your account be confident &amp; safely.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>




<div className="home1-location-area mb-120">
        <div className="container">
            <div className="row mb-60">
                <div className="col-12 d-flex flex-wrap align-items-end justify-content-md-between justify-content-start gap-3">
                    <div className="section-title1">
                        <h2>Get Dream With <span>Location</span></h2>
                        <p>
                          {region === 'us'
                            ? 'Explore top healthcare opportunities across 25 US states.'
                            : `Explore top healthcare opportunities across ${locationLabel}.`}
                        </p>
                    </div>
                    <div className="swiper-btn1 d-flex align-items-center">
                      <button
                        type="button"
                        className="left-btn prev-1"
                        onClick={() => scrollLocationTrack('prev')}
                        aria-label="Previous locations"
                      >
                        <img src="assets/images/icon/explore-elliose.svg" alt="" />
                      </button>
                      <button
                        type="button"
                        className="right-btn next-1"
                        onClick={() => scrollLocationTrack('next')}
                        aria-label="Next locations"
                      >
                        <img src="assets/images/icon/explore-elliose.svg" alt="" />
                      </button>
                    </div>
                </div>
            </div>
            <div
              ref={locationTrackRef}
              style={{
                display: 'flex',
                gap: '20px',
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollSnapType: 'x mandatory',
                paddingBottom: '8px',
              }}
            >
              {featuredStates.length === 0 ? (
                <div style={{ padding: '24px 0', color: '#5C6370' }}>
                  {`No job locations yet. Post a job to see top ${stateLabelPlural.toLowerCase()} here.`}
                </div>
              ) : (
                featuredStates.map((item, index) => {
                  const fallbackImage = `assets/images/bg/location-0${(index % 6) + 1}.png`;
                  const flagSrc = item.record
                    ? getRegionFlagUrl(region, item.record)
                    : fallbackImage;
                  return (
                    <div
                      key={item.state}
                      style={{
                        minWidth: '290px',
                        maxWidth: '290px',
                        flex: '0 0 auto',
                        scrollSnapAlign: 'start',
                      }}
                    >
                      <div className="location-card">
                        <div className="location-img">
                          <img
                            className="img-fluid"
                            src={flagSrc}
                            alt={`${item.state} flag`}
                          />
                        </div>
                        <div className="location-content text-center">
                          <h5>
                            <Link to={`/job-listing?state=${encodeURIComponent(item.state)}`}>
                              <img src="assets/images/icon/location.svg" alt="" />
                              {`${item.state}, ${locationLabel}`}
                            </Link>
                          </h5>
                          <p>
                            Job Available: <span>{item.jobs}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
        </div>
    </div>



<div className="home1-review-area mb-120">
        <div className="container">
            <div className="row mb-60">
                <div className="col-12 d-flex flex-wrap align-items-end justify-content-md-between justify-content-start gap-3">
                    <div className="section-title1">
                        <h2>Trusted User <span>Reviews</span></h2>
                        <p>To choose your trending job dream & to make future bright.</p>
                    </div>
                    <div className="swiper-btn1 d-flex align-items-center">
                        <div className="left-btn prev-2">
                            <img src="assets/images/icon/explore-elliose.svg" alt="" />
                        </div>
                        <div className="right-btn next-2">
                            <img src="assets/images/icon/explore-elliose.svg" alt="" />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="swiper testimonial-slider1">
                    <div className="swiper-wrapper">
                        <div className="swiper-slide">
                            <div className="testimonial-card">
                                <div className="author-img">
                                    <img src="assets/images/bg/testimonial-author1.png" alt="" />
                                    <div className="quat-icon">
                                        <img src="assets/images/icon/quat-icon.svg" alt="" />
                                    </div>
                                </div>
                                <div className="testimonial-content">
                                    <div className="author-review-area">
                                        <div className="author-area">
                                            <h5>Jacoline Frangly</h5>
                                            <span>React Developer</span>
                                        </div>
                                        <ul>
                                            <li><i className="bi bi-star-fill"></i></li>
                                            <li><i className="bi bi-star-fill"></i></li>
                                            <li><i className="bi bi-star-fill"></i></li>
                                            <li><i className="bi bi-star-fill"></i></li>
                                            <li><i className="bi bi-star-half"></i></li>
                                        </ul>
                                    </div>
                                    <p>On the other hand, we denounce with to the righteous indignation and dislike the men who are so beguiled to demoralized by the charms of pleasure.</p>
                                </div>
                            </div>
                        </div>
                        <div className="swiper-slide">
                            <div className="testimonial-card">
                                <div className="author-img">
                                    <img src="assets/images/bg/testimonial-author2.png" alt="" />
                                    <div className="quat-icon">
                                        <img src="assets/images/icon/quat-icon.svg" alt="" />
                                    </div>
                                </div>
                                <div className="testimonial-content">
                                    <div className="author-review-area">
                                        <div className="author-area">
                                            <h5>Jordan Miycol</h5>
                                            <span>UI/UX Engineer</span>
                                        </div>
                                        <ul>
                                            <li><i className="bi bi-star-fill"></i></li>
                                            <li><i className="bi bi-star-fill"></i></li>
                                            <li><i className="bi bi-star-fill"></i></li>
                                            <li><i className="bi bi-star-fill"></i></li>
                                            <li><i className="bi bi-star-half"></i></li>
                                        </ul>
                                    </div>
                                    <p>On the other hand, we denounce with to the righteous indignation and dislike the men who are so beguiled to demoralized by the charms of pleasure.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>



<div className="home1-trusted-company mb-120">
        <div className="container">
            <div className="row">
                <div className="col-lg-12">
                    <div className="section-title">
                        <h5>Our Trusted Company</h5>
                    </div>
                    <div className="swiper trusted-company-slider">
                      <div className="swiper-wrapper">
                        {(hospitals && hospitals.length > 0
                          ? hospitals.slice(0, 4)
                          : Array.from({ length: 4 }).map((_, idx) => ({ _id: `placeholder-${idx}`, logo: '' }))
                        ).map((hospital, idx) => (
                          <div className="swiper-slide" key={hospital._id || idx}>
                            <div className="company-logo">
                              <img
                                src={resolveHospitalLogo(hospital.logo)}
                                alt={hospital.name || 'Hospital'}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                </div>
            </div>
        </div>
    </div>




<div className="home1-top-recruiters mb-120">
        <div className="container">
            <div className="row mb-60">
                <div className="col-12 d-flex flex-wrap align-items-end justify-content-md-between justify-content-start gap-3">
                    <div className="section-title1">
                        <h2>Our Top <span>Recruiters</span></h2>
                        <p>Top recruiters by active openings, updated from live jobs.</p>
                    </div>
                </div>
            </div>
            <div className="row">
                {recruiterCards.length === 0 ? (
                  <div className="col-12">
                    <div className="recruters-card">
                      <div className="company-area">
                        <div className="name-location">
                          <h5>No recruiters available</h5>
                          <p>Top recruiters will appear once jobs are posted.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  recruiterCards.map((row) => (
                    <div className="col-lg-4 col-md-6 mb-4" key={row.key}>
                      <div className="recruters-card">
                        <div className="company-area">
                          <div className="logo">
                            <img src={row.hospitalLogo} alt={row.hospitalName} />
                          </div>
                          <div className="name-location">
                            <h5>
                              <Link to={`/job-listing?q=${encodeURIComponent(row.hospitalName)}`}>
                                {row.hospitalName}
                              </Link>
                            </h5>
                            <p>
                              <img src="assets/images/icon/location.svg" alt="" /> {row.location}
                            </p>
                            <p>
                              <img src="assets/images/icon/person-2.svg" alt="" /> Open Jobs: {row.openJobs}
                            </p>
                          </div>
                        </div>
                        <div className="job-details-vacancies">
                          <div className="apply-btn">
                            <Link to={`/job-listing?q=${encodeURIComponent(row.hospitalName)}`}>
                              <span>
                                <img src="assets/images/icon/apply-ellipse.svg" alt="" />
                              </span>
                              View Jobs
                            </Link>
                          </div>
                          <div className="vacancies">
                            <p>
                              Vacancies: <span>{row.openJobs}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
            </div>
        </div>
    </div>


<div className="home1-article-area mb-120">
  <div className="container">
    <div className="row mb-60">
      <div className="col-12 d-flex justify-content-center">
        <div className="section-title1 text-center">
          <h2>
            Portal Recent <span>Article</span>
          </h2>
          <p>To choose your trending job dream &amp; to make future bright.</p>
        </div>
      </div>
    </div>
    <div className="row g-lg-4 gy-5 justify-content-center">
      {homeBlogCards.length === 0 ? (
        <div className="col-lg-12 text-center">
          <p>No blogs published yet.</p>
        </div>
      ) : (
        homeBlogCards.map((blog) => (
          <div className="col-lg-4 col-md-6 col-sm-10" key={blog._id}>
            <div className="recent-article-wrap">
              <div className="recent-article-img">
                <img className="img-fluid" src={blog.image} alt={blog.title} />
                <div className="publish-area d-xl-none d-flex">
                  <Link to={`/blog-details/${blog.slug}`}>
                    <span>{String(dayNumber(blog.publishedAt)).padStart(2, '0')}</span>
                    {monthName(blog.publishedAt)}
                  </Link>
                </div>
              </div>
              <div className="recent-article-content">
                <div className="recent-article-meta">
                  <div className="publish-area">
                    <Link to={`/blog-details/${blog.slug}`}>
                      <span>{String(dayNumber(blog.publishedAt)).padStart(2, '0')}</span>
                      {monthName(blog.publishedAt)}
                    </Link>
                  </div>
                  <ul>
                    <li>
                      <Link to={`/blog-details/${blog.slug}`}>
                        <img src="assets/images/icon/comment.svg" alt="" />
                        {blog.commentsCount || 0} Comments
                      </Link>
                    </li>
                    <li>
                      <Link to={`/blog-details/${blog.slug}`}>
                        <img src="assets/images/icon/user.svg" alt="" />
                        {blog.authorName || 'System Admin'}
                      </Link>
                    </li>
                  </ul>
                </div>
                <h4>
                  <Link to={`/blog-details/${blog.slug}`}>{blog.title}</Link>
                </h4>
                <div className="explore-btn">
                  <Link to={`/blog-details/${blog.slug}`}>
                    <span>
                      <img src="assets/images/icon/explore-elliose.svg" alt="" />
                    </span>{' '}
                    Explore More
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
</div>






    </>
  )
}

export default Home

